<!-- ═══════════════════════════════════════════════════ -->
<!-- HERO                                                -->
<!-- ═══════════════════════════════════════════════════ -->
<section class="hero-section surface-contour" #heroSection>
  <div class="hero-bg" #heroBg aria-hidden="true"></div>
  <div class="container">
    <span class="text-label hero-eyebrow">Restless Society</span>
    <h1 class="hero-title text-display" #heroTitle aria-label="Restless By Nature">Restless By Nature</h1>
    <p class="hero-subtitle" #heroSubtitle>
      A gathering point for house and after-dark culture — the word on what's next,
      before it's announced anywhere else.
    </p>
    <button class="btn btn-primary cta-button" #heroCta appScrollTo data-target="#subscription-section">
      Join the community
    </button>

    <!-- Ticket-stub CTA — a perforated, torn-ticket shaped link into the
         events calendar. Sits inline in the hero so it's one of the first
         interactive things a visitor sees, without adding real weight to
         the section: it's a single compact strip, not a new hero panel. -->
    <a
      routerLink="/events"
      class="hero-ticket-cta"
      aria-label="View upcoming events and get tickets"
      #heroTicket
    >
      <span class="ticket-half ticket-half--stub">
        <span class="ticket-pulse" aria-hidden="true">
          <span class="ticket-pulse-dot"></span>
        </span>
        <span class="ticket-eq" aria-hidden="true">
          <span></span><span></span><span></span><span></span>
        </span>
        <span class="ticket-stub-text text-label">Doors&nbsp;Open</span>
      </span>

      <span class="ticket-divider" aria-hidden="true">
        <span class="ticket-notch ticket-notch--top"></span>
        <span class="ticket-notch ticket-notch--bottom"></span>
      </span>

      <span class="ticket-half ticket-half--action">
        <span class="ticket-marquee" aria-hidden="true">
          <span class="ticket-marquee-track">
            <span>UPCOMING&nbsp;EVENTS&nbsp;&nbsp;·&nbsp;&nbsp;GET&nbsp;TICKETS&nbsp;&nbsp;·&nbsp;&nbsp;</span>
            <span>UPCOMING&nbsp;EVENTS&nbsp;&nbsp;·&nbsp;&nbsp;GET&nbsp;TICKETS&nbsp;&nbsp;·&nbsp;&nbsp;</span>
          </span>
        </span>
        <span class="ticket-action-front">
          <span class="ticket-action-text">View Events</span>
          <i class="bi bi-arrow-right ticket-arrow"></i>
        </span>
      </span>
    </a>
  </div>
</section>

<!-- ═══════════════════════════════════════════════════ -->
<!-- MANIFESTO — pinned, scroll-scrubbed horizontal rack  -->
<!-- Vertical scroll drives a horizontal slide through the -->
<!-- mission/vision/identity statements. Same scroll-tied   -->
<!-- mechanic as the hero (GSAP ScrollTrigger, scrub, no    -->
<!-- timers) but a different move, so the two don't repeat  -->
<!-- the same trick twice on one page.                      -->
<!-- ═══════════════════════════════════════════════════ -->
<section class="home-section manifesto-section" #manifestoSection>
  <div class="container manifesto-head">
    <span class="text-label">Who we are</span>
    <h2 class="section-title text-heading-lg">About Restless Society</h2>
  </div>

  <div class="manifesto-viewport">
    <span class="manifesto-watermark" #manifestoWatermark aria-hidden="true">MANIFESTO</span>

    <div class="manifesto-track" #manifestoTrack>
      <article class="manifesto-card">
        <span class="manifesto-index text-label">01 — Mission</span>
        <p class="manifesto-statement">To protect authenticity, connect communities, and keep the underground spirit loud and honest.</p>
      </article>

      <article class="manifesto-card">
        <span class="manifesto-index text-label">02 — Vision</span>
        <p class="manifesto-statement">To grow into the most trusted voice for Nigeria's rave and underground scene — pure, connected, unapologetically alive.</p>
      </article>

      <article class="manifesto-card manifesto-card--tag">
        <span class="manifesto-index text-label">03</span>
        <p class="manifesto-tag text-display-md">House<br>heads.</p>
      </article>

      <article class="manifesto-card manifesto-card--tag">
        <span class="manifesto-index text-label">04</span>
        <p class="manifesto-tag text-display-md">Afro-tech<br>futurists.</p>
      </article>

      <article class="manifesto-card manifesto-card--tag">
        <span class="manifesto-index text-label">05</span>
        <p class="manifesto-tag text-display-md">Gqom<br>soldiers.</p>
      </article>

      <article class="manifesto-card manifesto-card--tag">
        <span class="manifesto-index text-label">06</span>
        <p class="manifesto-tag text-display-md">Deep house<br>souls.</p>
      </article>

      <article class="manifesto-card manifesto-card--final">
        <span class="manifesto-index text-label">07</span>
        <p class="manifesto-statement">You are part of the <span class="restless-highlight">RESTLESS</span> — a vital link in a growing network built on shared energy.</p>
        <a routerLink="/about" class="about-link">
          Read the full story
          <i class="bi bi-arrow-right"></i>
        </a>
      </article>
    </div>
  </div>

  <div class="container manifesto-foot">
    <div class="manifesto-dots" #manifestoDots aria-hidden="true">
      <span class="manifesto-dot active"></span>
      <span class="manifesto-dot"></span>
      <span class="manifesto-dot"></span>
      <span class="manifesto-dot"></span>
      <span class="manifesto-dot"></span>
      <span class="manifesto-dot"></span>
      <span class="manifesto-dot"></span>
    </div>
  </div>
</section>

<!-- ═══════════════════════════════════════════════════ -->
<!-- COMMUNITY NEWS                                       -->
<!-- ═══════════════════════════════════════════════════ -->
<section class="home-section news-section">
  <div class="container">
    <div class="section-header">
      <div>
        <span class="text-label">Word on the street</span>
        <h2 class="section-title text-heading-lg">Community News</h2>
      </div>
      <a routerLink="/community-news" class="section-link">
        All news <i class="bi bi-arrow-right"></i>
      </a>
    </div>
    <app-community-news-list [limit]="3"></app-community-news-list>
  </div>
</section>

<!-- ═══════════════════════════════════════════════════ -->
<!-- PLAYLISTS                                            -->
<!-- ═══════════════════════════════════════════════════ -->
<section class="home-section playlists-section">
  <div class="container">
    <div class="section-header">
      <div>
        <span class="text-label">Now playing</span>
        <h2 class="section-title text-heading-lg">Monthly Playlists</h2>
      </div>
      <a routerLink="/playlists" class="section-link">
        All playlists <i class="bi bi-arrow-right"></i>
      </a>
    </div>
    <app-playlist-grid [limit]="4"></app-playlist-grid>
  </div>
</section>

<!-- ═══════════════════════════════════════════════════ -->
<!-- CONNECT WITH US                                      -->
<!-- ═══════════════════════════════════════════════════ -->
<section class="social-media-section">
  <div class="container">

    <div class="social-header">
      <span class="text-label">Find us</span>
      <h2 class="section-title text-heading-lg">Connect With Us</h2>
      <p class="section-description">Find us everywhere the underground lives</p>
    </div>

    <!-- Cursor-reactive panel: a soft glow tracks the real pointer
         position inside this section and the QR card / social links
         pull toward it (spring back on leave) — a different animation
         mechanic from the hero/manifesto's scroll-scrubbed reveals
         above. Wired up in setupMagneticSocial() in the .ts file;
         gated off entirely on touch devices and prefers-reduced-motion,
         where this just renders as the plain static panel below. -->
    <div class="circle-panel" #magneticZone>
      <div class="magnetic-cursor" #magneticCursor aria-hidden="true"></div>

      <div class="qr-block">
        <div class="magnetic-target magnetic-target--qr" data-magnetic-strength="0.25" data-magnetic-max="20">
          <a href="https://chat.whatsapp.com/I1uaM1L6VyS0EtuBfNETuI"
             target="_blank" rel="noopener"
             class="qr-card"
             aria-label="Scan or tap to join WhatsApp Community">
            <div class="qr-corner qr-corner--tl"></div>
            <div class="qr-corner qr-corner--tr"></div>
            <div class="qr-corner qr-corner--bl"></div>
            <div class="qr-corner qr-corner--br"></div>
            <img src="/QRcode.jpg" alt="WhatsApp Community QR Code" class="qr-code-image" width="140" height="140">
          </a>
        </div>

        <!-- Mobile only: icon-only row underneath the QR image -->
        <div class="qr-social-mobile">
          <a href="https://www.instagram.com/the.restless.society?igsh=MWR5ZTB4ajZ5cWZ2Ng%3D%3D&utm_source=qr"
             target="_blank" rel="noopener" class="qr-social-icon" title="Instagram" aria-label="Instagram">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a href="https://x.com/restlessociety?s=11"
             target="_blank" rel="noopener" class="qr-social-icon" title="X (Twitter)" aria-label="X (Twitter)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://www.tiktok.com/@restless.society?_r=1&_t=ZS-93cBFfyrScb"
             target="_blank" rel="noopener" class="qr-social-icon" title="TikTok" aria-label="TikTok">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
        </div>
      </div>

      <div class="circle-text">
        <span class="text-label">// The circle</span>
        <h3 class="circle-title">Scan. Join.<br>Stay restless.</h3>
        <p class="circle-desc">Scan the code or tap it to enter the WhatsApp community.</p>
      </div>

      <!-- Desktop only: labeled column beside the QR card + text -->
      <div class="social-links">
        <div class="magnetic-target" data-magnetic-strength="0.4" data-magnetic-max="16">
          <a href="https://www.instagram.com/the.restless.society?igsh=MWR5ZTB4ajZ5cWZ2Ng%3D%3D&utm_source=qr"
             target="_blank" rel="noopener" class="social-link" title="Instagram">
            <span class="icon-face">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </span>
            <span class="icon-label">Instagram</span>
          </a>
        </div>

        <div class="magnetic-target" data-magnetic-strength="0.4" data-magnetic-max="16">
          <a href="https://x.com/restlessociety?s=11"
             target="_blank" rel="noopener" class="social-link" title="X (Twitter)">
            <span class="icon-face">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </span>
            <span class="icon-label">Twitter</span>
          </a>
        </div>

        <div class="magnetic-target" data-magnetic-strength="0.4" data-magnetic-max="16">
          <a href="https://www.tiktok.com/@restless.society?_r=1&_t=ZS-93cBFfyrScb"
             target="_blank" rel="noopener" class="social-link" title="TikTok">
            <span class="icon-face">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </span>
            <span class="icon-label">TikTok</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════════════════════════════════════════════ -->
<!-- NEWSLETTER                                           -->
<!-- ═══════════════════════════════════════════════════ -->
<section id="subscription-section" class="home-section subscription-section">
  <div class="container">
    <span class="text-label">Transmission</span>
    <h2 class="section-title text-heading-lg">Join the community</h2>
    <p class="section-description">
      Stay updated with the latest news, events, and exclusive content from Restless Society.
      Prefer a focused view? Visit the <a routerLink="/newsletter" class="inline-link">newsletter page</a>.
    </p>
    <app-newsletter-form (subscribed)="onSubscribed()"></app-newsletter-form>
  </div>
</section>